import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, merge, Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import type { AccountPasswordForm } from '@core/components-authorization-legacy';
import { PasswordChangeService } from '@core/components-authorization-legacy';
import { I18nService } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import type { TellentPasswordChange, TellentPasswordChangeResponse } from '@core/tellent-types';
import { TellentComponentsNotificationService } from '@core/tellent-util';

import { CurrentUserAnalyticsService } from '../current-user-profile-dialog/current-user.analytics';

@Injectable({
  providedIn: 'root',
})
export class TellentAdminPasswordChangeService extends PasswordChangeService {
  private readonly i18nService = inject(I18nService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(TellentComponentsNotificationService);
  private readonly analytics = inject(CurrentUserAnalyticsService);

  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);

  constructor() {
    super();
    this.init();
  }

  public showSuccessNotification(): void {
    this.notificationService.success(
      this.i18nService.translate('tlc.current_user.admin_password_form.password_changed'),
    );
  }

  public submit(form: AccountPasswordForm): Observable<TellentPasswordChangeResponse> {
    const { password, passwordConfirmation, secureChangeToken, currentPassword } =
      form.getRawValue();

    const passwordChange: TellentPasswordChange = {
      password: password,
      passwordConfirmation: passwordConfirmation,
    };

    if (secureChangeToken) {
      passwordChange.code = <string>secureChangeToken;
    }

    if (currentPassword) {
      passwordChange.currentPassword = <string>currentPassword;
    }

    this.currentUserFacade.updatePassword(passwordChange);

    this.analytics.trackPasswordChanged();

    return merge(
      this.currentUserFacade.updatePasswordSuccess$.pipe(map(action => action.payload)),
      this.currentUserFacade.updatePasswordFail$.pipe(
        switchMap(action => throwError(() => action.payload)),
      ),
    );
  }

  private init(): void {
    this.errors = {
      required: this.i18nService.translate(
        'tlc.current_user.admin_password_form.password_required',
      ),
      mismatchedPasswords: this.i18nService.translate(
        'tlc.current_user.admin_password_form.password_mismatched',
      ),
    };
  }
}
